/**
 * Store Pinia d'authentification pour la plateforme Anamnesis
 * Gère l'état global d'authentification, la session et la persistence
 */

import { apiClient } from '@/services/api';
import { authService } from '@/services/authService';
import { keycloakService } from '@/services/keycloakService';
import type {
  AdminResetPasswordRequest,
  AuthError,
  ChangePasswordRequest,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  TokenConfig,
  User
} from '@/types/auth';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

// Type pour le provider d'authentification
export type AuthProvider = 'jwt' | 'keycloak';

// Configuration du token
const TOKEN_CONFIG: TokenConfig = {
  storageKey: 'anamnesis_auth_token',
  expiryBuffer: 5, // Minutes avant expiration pour avertir
  maxAge: 30, // Durée de vie en minutes (correspond au backend)
};

// Clé de stockage pour le provider d'authentification
const AUTH_PROVIDER_KEY = 'anamnesis_auth_provider';

/**
 * Store d'authentification utilisant la Composition API de Pinia
 */
export const useAuthStore = defineStore('auth', () => {
  // ===============================
  // État (state)
  // ===============================

  const user = ref<User | null>(null);
  const token = ref<string | null>(null);
  const authProvider = ref<AuthProvider>('jwt');
  const keycloakEnabled = ref<boolean>(false);
  
  // États de chargement
  const isLoading = ref(false);
  const isLoggingIn = ref(false);
  const isRegistering = ref(false);
  const isChangingPassword = ref(false);
  const isFetchingUser = ref(false);
  
  // Cache pour éviter les requêtes multiples
  const lastUserFetch = ref<number>(0);
  const userFetchCache = 30000; // 30 secondes
  const pendingUserFetch = ref<Promise<User> | null>(null);
  
  // Gestion d'erreurs
  const error = ref<string | null>(null);
  
  // Session
  const lastActivity = ref<Date | null>(null);
  const tokenExpiry = ref<Date | null>(null);

  // ===============================
  // Getters (computed)
  // ===============================
  
  const isAuthenticated = computed((): boolean => {
    return !!(token.value && user.value && !isTokenExpired.value);
  });

  const isAdmin = computed((): boolean => {
    return user.value?.role === 'admin' || user.value?.role === 'superadmin' || false;
  });

  const isSuperAdmin = computed((): boolean => {
    return user.value?.role === 'superadmin' || false;
  });

  const userName = computed((): string | null => {
    return user.value?.name || null;
  });

  const userEmail = computed((): string | null => {
    return user.value?.email || null;
  });

  const hasValidToken = computed((): boolean => {
    return !!(token.value && !isTokenExpired.value);
  });

  const isTokenExpired = computed((): boolean => {
    if (!token.value) return true;
    return authService.isTokenExpired(token.value);
  });

  const timeUntilExpiry = computed((): number | null => {
    if (!token.value) return null;
    return authService.getTimeUntilExpiry(token.value);
  });

  const shouldWarnExpiry = computed((): boolean => {
    const timeRemaining = timeUntilExpiry.value;
    if (!timeRemaining) return false;

    const warningThreshold = TOKEN_CONFIG.expiryBuffer * 60 * 1000; // Convertir en ms
    return timeRemaining <= warningThreshold && timeRemaining > 0;
  });

  const isKeycloakEnabled = computed((): boolean => {
    return keycloakEnabled.value;
  });

  const isKeycloakUser = computed((): boolean => {
    return authProvider.value === 'keycloak';
  });

  // ===============================
  // Helpers de Permissions
  // ===============================

  /**
   * Vérifie si l'utilisateur peut éditer un asset
   */
  const canEditAsset = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return isAdmin.value || asset.user_id === user.value.uuid;
    };
  });

  /**
   * Vérifie si l'utilisateur peut supprimer un asset
   */
  const canDeleteAsset = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return isAdmin.value || asset.user_id === user.value.uuid;
    };
  });

  /**
   * Vérifie si l'utilisateur peut voir un asset
   */
  const canViewAsset = computed((): ((asset: { user_id: string; is_public: boolean }) => boolean) => {
    return (asset: { user_id: string; is_public: boolean }): boolean => {
      if (!user.value) return asset.is_public;
      return isAdmin.value || asset.user_id === user.value.uuid || asset.is_public;
    };
  });

  /**
   * Vérifie si l'utilisateur est propriétaire d'un asset
   */
  const isAssetOwner = computed((): ((asset: { user_id: string }) => boolean) => {
    return (asset: { user_id: string }): boolean => {
      if (!user.value) return false;
      return asset.user_id === user.value.uuid;
    };
  });

  // ===============================
  // Actions
  // ===============================

  /**
   * Initialise le store depuis le localStorage au démarrage
   */
  const initializeFromStorage = async (): Promise<void> => {
    // Vérifier si Keycloak est activé
    try {
      keycloakEnabled.value = await keycloakService.isEnabled();
    } catch (error) {
      console.warn('Impossible de vérifier la configuration Keycloak:', error);
      keycloakEnabled.value = false;
    }

    // Restaurer le provider d'authentification
    const storedProvider = localStorage.getItem(AUTH_PROVIDER_KEY) as AuthProvider | null;
    if (storedProvider && (storedProvider === 'jwt' || storedProvider === 'keycloak')) {
      authProvider.value = storedProvider;
    }

    const storedToken = localStorage.getItem(TOKEN_CONFIG.storageKey);

    if (storedToken) {
      // Vérifier si le token n'est pas expiré
      if (!authService.isTokenExpired(storedToken)) {
        token.value = storedToken;
        updateTokenExpiry();

        // Récupérer les infos utilisateur de manière synchrone
        try {
          await fetchCurrentUser();
          console.log('État d\'authentification restauré depuis le localStorage');
        } catch (error) {
          // Si échec, nettoyer le token invalide
          console.warn('Token invalide détecté lors de l\'initialisation, nettoyage...', error);
          logout();
        }
      } else {
        // Token expiré, le supprimer
        console.log('Token expiré détecté lors de l\'initialisation, suppression...');
        localStorage.removeItem(TOKEN_CONFIG.storageKey);
        localStorage.removeItem(AUTH_PROVIDER_KEY);
      }
    }
  };

  /**
   * Met à jour l'heure d'expiration du token
   */
  const updateTokenExpiry = (): void => {
    if (!token.value) {
      tokenExpiry.value = null;
      return;
    }

    const payload = authService.parseJwtToken(token.value);
    if (payload?.exp) {
      tokenExpiry.value = new Date(payload.exp * 1000);
    }
  };

  /**
   * Met à jour la dernière activité
   */
  const updateLastActivity = (): void => {
    lastActivity.value = new Date();
  };

  /**
   * Configure l'intercepteur d'authentification pour l'API client
   */
  const setupApiInterceptors = (): void => {
    apiClient.setInterceptors({
      onRequest: async (options) => {
        // Ajouter automatiquement le token d'auth si disponible
        if (token.value && hasValidToken.value) {
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${token.value}`,
          };
        }
        
        updateLastActivity();
        return options;
      },
      onError: async (error) => {
        // Déconnexion automatique si token expiré
        if (error.status === 401 && isAuthenticated.value) {
          console.warn('Token expiré détecté, déconnexion automatique');
          logout();
        }
        
        throw error;
      },
    });
  };

  /**
   * Sauvegarde le token dans le localStorage
   */
  const saveToken = (authToken: string, provider: AuthProvider = 'jwt'): void => {
    token.value = authToken;
    authProvider.value = provider;
    localStorage.setItem(TOKEN_CONFIG.storageKey, authToken);
    localStorage.setItem(AUTH_PROVIDER_KEY, provider);
    updateTokenExpiry();
    setupApiInterceptors();
  };

  /**
   * Supprime le token du localStorage
   */
  const removeToken = (): void => {
    token.value = null;
    tokenExpiry.value = null;
    localStorage.removeItem(TOKEN_CONFIG.storageKey);
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  };

  /**
   * Connexion utilisateur
   */
  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    isLoggingIn.value = true;
    error.value = null;

    try {
      const response = await authService.login(credentials);
      
      // Sauvegarder le token
      saveToken(response.access_token);
      
      // Si le backend retourne les infos utilisateur, les utiliser
      if (response.user) {
        user.value = response.user;
        console.log(`Connexion réussie pour ${response.user.email}`);
      } else {
        // Sinon, récupérer les infos utilisateur avec le token
        console.log('Connexion réussie, récupération des informations utilisateur...');
        try {
          const userData = await authService.getCurrentUser(response.access_token);
          user.value = userData;
          console.log(`Informations utilisateur récupérées pour ${userData.email}`);
        } catch (userError) {
          console.error('Erreur lors de la récupération des informations utilisateur:', userError);
          // Ne pas faire échouer le login si on ne peut pas récupérer les infos user
          // L'utilisateur sera redirigé et les infos seront récupérées plus tard
        }
      }
      
      return response;
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de connexion:', authError);
      throw authError;
      
    } finally {
      isLoggingIn.value = false;
    }
  };

  /**
   * Inscription utilisateur
   */
  const register = async (userData: RegisterRequest): Promise<RegisterResponse> => {
    isRegistering.value = true;
    error.value = null;

    try {
      const response = await authService.register(userData);
      
      console.log(`Inscription réussie pour ${userData.email}`, {
        emailSent: response.email_sent,
        userUuid: response.user_uuid,
      });
      
      return response;
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur d\'inscription:', authError);
      throw authError;
      
    } finally {
      isRegistering.value = false;
    }
  };

  /**
   * Récupération du profil utilisateur actuel avec cache
   */
  const fetchCurrentUser = async (forceRefresh = false): Promise<User> => {
    if (!token.value) {
      throw new Error('Aucun token d\'authentification');
    }

    // Vérifier le cache si pas de force refresh
    const now = Date.now();
    if (!forceRefresh && user.value && lastUserFetch.value > 0 && (now - lastUserFetch.value) < userFetchCache) {
      return user.value;
    }

    // Si une requête est déjà en cours, attendre son résultat
    if (pendingUserFetch.value) {
      return pendingUserFetch.value;
    }

    isFetchingUser.value = true;
    error.value = null;

    // Créer la promesse de requête
    pendingUserFetch.value = (async (): Promise<User> => {
      try {
        const userData = await authService.getCurrentUser(token.value!);
        user.value = userData;
        lastUserFetch.value = now;
        
        return userData;
        
      } catch (err) {
        const authError = err as AuthError;
        error.value = authError.message;
        
        console.error('Erreur de récupération utilisateur:', authError);
        
        // Si token invalide, déconnecter
        if (authError.code === 'TOKEN_EXPIRED') {
          logout();
        }
        
        throw authError;
        
      } finally {
        isFetchingUser.value = false;
        pendingUserFetch.value = null;
      }
    })();

    return pendingUserFetch.value;
  };

  /**
   * Changement de mot de passe
   */
  const changePassword = async (passwords: ChangePasswordRequest): Promise<void> => {
    if (!token.value) {
      throw new Error('Non authentifié');
    }

    isChangingPassword.value = true;
    error.value = null;

    try {
      await authService.changePassword(passwords, token.value);
      
      console.log('Mot de passe changé avec succès');
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de changement de mot de passe:', authError);
      throw authError;
      
    } finally {
      isChangingPassword.value = false;
    }
  };

  /**
   * Réinitialisation de mot de passe par admin (pour les admins uniquement)
   */
  const resetUserPassword = async (data: AdminResetPasswordRequest): Promise<void> => {
    if (!token.value) {
      throw new Error('Non authentifié');
    }

    if (!isAdmin.value) {
      throw new Error('Permissions insuffisantes');
    }

    isLoading.value = true;
    error.value = null;

    try {
      await authService.adminResetPassword(data, token.value);
      
      console.log(`Mot de passe réinitialisé pour l'utilisateur ${data.user_uuid}`);
      
    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;
      
      console.error('Erreur de réinitialisation admin:', authError);
      throw authError;
      
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Demande de réinitialisation de mot de passe (mot de passe oublié)
   */
  const requestPasswordReset = async (email: string): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      await authService.requestPasswordReset(email);
      console.log(`Demande de réinitialisation de mot de passe envoyée pour ${email}`);

    } catch (err) {
      const authError = err as AuthError;
      error.value = authError.message;

      console.error('Erreur de demande de réinitialisation:', authError);
      throw authError;

    } finally {
      isLoading.value = false;
    }
  };

  // ===============================
  // Actions Keycloak
  // ===============================

  /**
   * Initie la connexion via Keycloak
   * Redirige vers la page de connexion Keycloak
   */
  const loginWithKeycloak = async (): Promise<void> => {
    if (!keycloakEnabled.value) {
      throw new Error('L\'authentification Keycloak n\'est pas activée');
    }

    isLoggingIn.value = true;
    error.value = null;

    try {
      await keycloakService.login();
      // La redirection va se produire, pas besoin de faire quoi que ce soit d'autre
    } catch (err) {
      isLoggingIn.value = false;
      error.value = 'Erreur lors de la redirection vers Keycloak';
      console.error('Erreur Keycloak login:', err);
      throw err;
    }
  };

  /**
   * Traite le callback OAuth de Keycloak
   * À appeler depuis la page de callback
   */
  const handleKeycloakCallback = async (): Promise<LoginResponse> => {
    isLoggingIn.value = true;
    error.value = null;

    try {
      const response = await keycloakService.handleCallback();

      // Sauvegarder le token avec le provider Keycloak
      saveToken(response.access_token, 'keycloak');

      // Stocker les informations utilisateur
      if (response.user) {
        user.value = response.user;
        console.log(`Connexion Keycloak réussie pour ${response.user.email}`);
      }

      return response;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur lors de l\'authentification Keycloak';
      error.value = errorMessage;

      console.error('Erreur Keycloak callback:', err);
      throw err;

    } finally {
      isLoggingIn.value = false;
    }
  };

  /**
   * Vérifie si Keycloak est activé et met à jour l'état
   */
  const checkKeycloakEnabled = async (): Promise<boolean> => {
    try {
      keycloakEnabled.value = await keycloakService.isEnabled();
      return keycloakEnabled.value;
    } catch (error) {
      console.warn('Impossible de vérifier la configuration Keycloak:', error);
      keycloakEnabled.value = false;
      return false;
    }
  };

  /**
   * Déconnexion
   * Si l'utilisateur était connecté via Keycloak, effectue aussi la déconnexion SSO
   */
  const logout = async (skipKeycloakLogout = false): Promise<void> => {
    console.log('Déconnexion de l\'utilisateur');

    const wasKeycloakUser = authProvider.value === 'keycloak';

    // Nettoyer l'état
    user.value = null;
    removeToken();
    lastActivity.value = null;
    error.value = null;
    authProvider.value = 'jwt';

    // Nettoyer le cache
    lastUserFetch.value = 0;
    pendingUserFetch.value = null;

    // Réinitialiser les états de chargement
    isLoading.value = false;
    isLoggingIn.value = false;
    isRegistering.value = false;
    isChangingPassword.value = false;
    isFetchingUser.value = false;

    // Si l'utilisateur était connecté via Keycloak, effectuer la déconnexion SSO
    if (wasKeycloakUser && !skipKeycloakLogout && keycloakEnabled.value) {
      try {
        await keycloakService.logout();
        // La redirection va se produire
      } catch (err) {
        console.warn('Erreur lors de la déconnexion Keycloak:', err);
        // Continuer même en cas d'erreur
      }
    }
  };

  /**
   * Déconnexion locale uniquement (sans déconnexion Keycloak)
   */
  const logoutLocal = (): void => {
    logout(true);
  };

  /**
   * Vérification périodique de l'expiration du token
   */
  const checkTokenExpiry = (): boolean => {
    if (!token.value) return false;
    
    if (isTokenExpired.value) {
      console.warn('Token expiré détecté lors de la vérification');
      logout();
      return false;
    }
    
    return true;
  };

  /**
   * Rafraîchissement des données utilisateur
   */
  const refreshUserData = async (): Promise<void> => {
    if (!hasValidToken.value) return;
    
    try {
      await fetchCurrentUser();
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données utilisateur:', error);
    }
  };

  /**
   * Efface l'erreur actuelle
   */
  const clearError = (): void => {
    error.value = null;
  };

  // ===============================
  // Initialisation
  // ===============================
  
  // Configuration des intercepteurs API au chargement du store
  setupApiInterceptors();

  // ===============================
  // Interface du store
  // ===============================
  
  return {
    // État
    user,
    token,
    authProvider,
    keycloakEnabled,
    isLoading,
    isLoggingIn,
    isRegistering,
    isChangingPassword,
    isFetchingUser,
    error,
    lastActivity,
    tokenExpiry,

    // Getters
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    userName,
    userEmail,
    hasValidToken,
    isTokenExpired,
    timeUntilExpiry,
    shouldWarnExpiry,
    isKeycloakEnabled,
    isKeycloakUser,

    // Helpers de Permissions
    canEditAsset,
    canDeleteAsset,
    canViewAsset,
    isAssetOwner,

    // Actions
    initializeFromStorage,
    updateLastActivity,
    setupApiInterceptors,
    login,
    register,
    fetchCurrentUser,
    changePassword,
    resetUserPassword,
    requestPasswordReset,
    logout,
    logoutLocal,
    checkTokenExpiry,
    refreshUserData,
    clearError,

    // Actions Keycloak
    loginWithKeycloak,
    handleKeycloakCallback,
    checkKeycloakEnabled,
  };
});

// Export des types pour utilisation externe
export type AuthStore = ReturnType<typeof useAuthStore>; 